
import { House, Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { Tables } from "@/integrations/supabase/types";
import { useState, useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";

interface PropertyCardImageProps {
  property: Tables<"real_estate_properties">;
}

export const PropertyCardImage = ({ property }: PropertyCardImageProps) => {
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (property.images?.[0]) {
      const img = new Image();
      img.src = property.images[0];
      img.onload = () => setIsImageLoaded(true);
    }
  }, [property.images]);

  return (
    <div className="aspect-video relative bg-gradient-to-br from-primary/5 to-primary/10 overflow-hidden">
      {property.images?.[0] ? (
        <>
          {!isImageLoaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 animate-pulse">
              <House className="h-12 w-12 text-muted-foreground/50" />
            </div>
          )}
          <img
            src={property.images[0]}
            alt={property.title}
            className={`w-full h-full object-cover transition-opacity duration-300 ${
              isImageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            loading={isMobile ? "lazy" : "eager"}
          />
        </>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <House className="h-12 w-12 text-muted-foreground/50" />
        </div>
      )}
      
      {/* Top right badges */}
      <div className={`absolute top-2 right-2 flex gap-2 ${isMobile ? 'flex-col' : 'flex-row'}`}>
        <Badge className="bg-white/90 backdrop-blur-sm text-slate-700 border-slate-200">
          {property.category || 'Residential'}
        </Badge>
        {property.is_featured && (
          <Badge className="bg-yellow-500/90 backdrop-blur-sm flex items-center">
            <Star className="h-3 w-3 mr-1 fill-current" /> Featured
          </Badge>
        )}
      </div>
      
      {/* Bottom left financing badge */}
      <div className="absolute bottom-2 left-2">
        {property.collateral_value && (
          <Badge className="bg-purple-600/90 backdrop-blur-sm">
            Financing Available
          </Badge>
        )}
      </div>
      
      {/* Price tag */}
      <div className="absolute bottom-2 right-2">
        <Badge className="bg-green-600/90 backdrop-blur-sm px-3 py-1 text-sm">
          GHS {property.price.toLocaleString()}
        </Badge>
      </div>
    </div>
  );
};
